import { onMounted, watch } from 'vue';

/**
 * Composable for dynamically scaling the font size of an element to fit within its container.
 *
 * @param {Ref<HTMLElement>} elementRef - A Vue ref pointing to the HTML element whose font size needs scaling.
 * @param {Ref<any> | ComputedRef<any>} watchSource - A Vue ref or computed property to watch for changes that should trigger rescaling.
 * @param {number} [defaultFontSize=48] - The initial font size to start scaling down from.
 * @param {number} [maxWidthPercentage=80] - The maximum percentage of the container width the text should occupy before scaling down.
 */
const useDynamicFontSize = (elementRef, watchSource, defaultFontSize = 48, maxWidthPercentage = 80) => {
  const scaleFontSizeToContainer = () => {
    const element = elementRef.value;
    if (!element || !element.parentElement) return;

    let fontSize = defaultFontSize;
    element.style.fontSize = `${fontSize}px`; // Reset font size for accurate measurement

    const maxWidth = element.parentElement.clientWidth * (maxWidthPercentage / 100);

    // Iteratively reduce font size until the text fits
    while (element.scrollWidth > maxWidth && fontSize > 0) {
      fontSize -= 1;
      element.style.fontSize = `${fontSize}px`;
    }
  };

  // Watch for changes in the source ref/computed and rescale
  watch(watchSource, () => {
    // Use nextTick to ensure the DOM has updated after the watchSource change
    // before attempting to measure and scale.
    requestAnimationFrame(() => {
      scaleFontSizeToContainer();
    });
  });

  // Scale font size when the component mounts
  onMounted(() => {
    // Ensure the element is rendered before scaling
    requestAnimationFrame(() => {
        scaleFontSizeToContainer();
    });
  });

  // Expose the scaling function if manual triggering is needed (optional)
  return { scaleFontSizeToContainer };
};

export default useDynamicFontSize;
